import React from 'react';
import { useHomePage } from '../hooks/usePage';
import { renderPageSections } from '../lib/sectionMapper';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { SEO } from '../components/shared/SEO';
import { PageSkeleton } from '../components/ui/Skeleton';
import { useTenant } from '../hooks/useTenant';
import { useLanguage } from '../hooks/useLanguage';
import { generateOrganizationStructuredData, generateSearchActionStructuredData } from '../utils/structuredData';

const HomePage: React.FC = () => {
  const { page, loading, error, refetch } = useHomePage();
  const { config } = useTenant();
  const { t } = useLanguage();

  // Estado de carga
  if (loading) {
    return <PageSkeleton message={t('common.loading')} />;
  }

  // Estado de error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorState
          message={error}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // Si no hay página o no está publicada
  if (!page || page.status !== 'PUBLISHED') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <EmptyState
          title={t('common.pageNotAvailable')}
          description={t('common.pageNotAvailable')}
        />
      </div>
    );
  }

  // Si no hay secciones habilitadas
  if (!page.sections || page.sections.filter(s => s.enabled).length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <EmptyState
          title={t('common.pageNotAvailable')}
          description={t('common.pageNotAvailable')}
        />
      </div>
    );
  }


  // Generate structured data
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://example.com';

  const structuredDataItems = [];

  if (config) {
    // Organization structured data
    const organizationData = generateOrganizationStructuredData(
      config.name,
      config.description || config.seo.description,
      config.logo || '/logo.png',
      baseUrl,
      {
        phone: config.contact.phone,
        email: config.contact.email,
        address: config.contact.address,
      },
      config.social
    );
    structuredDataItems.push(organizationData);

    // Search action structured data
    const searchActionData = generateSearchActionStructuredData(baseUrl);
    structuredDataItems.push(searchActionData);
  }

  return (
    <>
      <SEO
        title={page.seoTitle || page.title}
        description={page.seoDescription || page.description}
        keywords={page.seoKeywords}
        canonicalPath="/"
        ogImage={page.ogImage}
        structuredData={structuredDataItems}
      />
      <main className="min-h-screen">
        {/* Renderizar todas las secciones de la página */}
        {renderPageSections(page.sections)}
      </main>
    </>
  );
};

export default HomePage;